import VdpPage, { PresentVdpPageProps } from 'components/VdpPage';
import { fetchAdInfo, fetchVehicleWithExperience } from 'lib/AutolistAPI';
import getIpAddress from 'lib/getIpAddress';
import {
  buildVdpPageProps,
  extractVehicleWithExperienceParamsFromQuery,
} from 'lib/hooks/useBuildVdpPageProps';
import i18next from 'lib/i18next';
import { serverSideMaxMindGeolocate } from 'lib/location/Geolocate';
import { inIframe } from 'lib/Utilities';
import { GetServerSideProps } from 'next';
import { useEffect } from 'react';
import XOR from 'lib/types/xor';
import Layout, { LayoutProps } from 'components/Layout';
import { useTranslation } from 'react-i18next';
import DirectToVdpContext from 'contexts/DirectToVdpContext';
import { useRouter } from 'next/router';
import { useSetAtom } from 'jotai';
import vehicleNotFoundAtom from 'atoms/VehicleNotFoundAtom';

export type VdpOrNotFoundParams = {
  vin: string;
};

export const VehicleNotFound = (): JSX.Element => {
  const router = useRouter();
  const setVehicleNotFound = useSetAtom(vehicleNotFoundAtom);

  useEffect(() => {
    setVehicleNotFound(true);
    router.push('/listings');

    if (inIframe()) {
      window.parent.postMessage('not found', '*');
    }
  }, [router, setVehicleNotFound]);
  return <div />;
};

type VdpOrNotFoundGetServerSideProps = GetServerSideProps<VdpOrNotFoundProps, VdpOrNotFoundParams>;

export const getServerSideProps: VdpOrNotFoundGetServerSideProps = async ({
  query,
  req,
  res,
  locale = i18next.language,
}) => {
  const ip = await getIpAddress(req);
  let reqLocation;
  if (ip) {
    reqLocation = await serverSideMaxMindGeolocate(ip);
  }

  const params = {
    latitude: reqLocation?.latitude,
    longitude: reqLocation?.longitude,
    locale,
    ...extractVehicleWithExperienceParamsFromQuery(query),
  };

  let vehicleWithExp;
  try {
    vehicleWithExp = await fetchVehicleWithExperience(params);
  } catch (e) {
    console.error(e);
  }

  if (!vehicleWithExp || !vehicleWithExp.vehicle || !vehicleWithExp.vehicle.active)
    return { props: { vehicle: null } };

  let adInfo;
  try {
    adInfo = await fetchAdInfo(params.vin);
  } catch (e) {
    console.error(e);
  }

  if (process.env.DISABLE_VDP_IFRAME_SECURITY === 'true') {
    res.removeHeader('x-frame-options');
  }
  res.setHeader('Cache-Control', 'public, max-age=900, must-revalidate');

  return {
    props: buildVdpPageProps(query, vehicleWithExp, adInfo),
  };
};

export type VdpOrNotFoundProps = XOR<PresentVdpPageProps, { vehicle: null }>;

const VdpOrNotFound = (props: VdpOrNotFoundProps): JSX.Element => {
  const { t } = useTranslation();

  const { asPath } = useRouter();
  const isDirectToVdp = !asPath.includes('vin');

  const getVdpLayoutProps = (): LayoutProps => ({
    title: props.vehicle
      ? `${props.vehicle.year} ${props.vehicle.make} ${props.vehicle.model}`
      : t(`main.meta.pageTitle`),
    description: '',
    includeAds: true,
    indexed: false,
    footer: <div />,
    showHeader: false,
  });

  const layoutProps = getVdpLayoutProps();
  if (props?.vehicle && props?.experience) {
    return (
      <DirectToVdpContext.Provider value={{ isDirectToVdp }}>
        <Layout {...layoutProps}>
          <VdpPage {...props} />
        </Layout>
      </DirectToVdpContext.Provider>
    );
  }
  return VehicleNotFound();
};

export default VdpOrNotFound;
